// CAPI (Conversions API) readiness utilities.
// Captures Meta _fbp, TikTok _ttp cookies, page URL, and formats fbc from fbclid.

/**
 * Read a named cookie from document.cookie.
 * Returns the cookie value or undefined if not found / unavailable.
 */
function getCookie(name: string): string | undefined {
  if (typeof document === 'undefined' || !document.cookie) return undefined;

  // Cookies are separated by "; " — match the exact name to avoid
  // partial matches (e.g. "x_fbp" matching "_fbp").
  const prefix = `${name}=`;
  const cookies = document.cookie.split('; ');
  for (const cookie of cookies) {
    if (cookie.startsWith(prefix)) {
      return decodeURIComponent(cookie.slice(prefix.length));
    }
  }
  return undefined;
}

/**
 * Read Meta's _fbp cookie.
 * Format set by Meta Pixel: fb.1.{timestamp}.{random}
 */
export function getFbpCookie(): string | undefined {
  return getCookie('_fbp');
}

// In-memory cache so we generate at most once per page load.
let generatedFbp: string | undefined;

/**
 * Get or create the _fbp value.
 * If the Meta Pixel has already set the `_fbp` cookie, returns it.
 * Otherwise generates one in Meta's format and sets it as a first-party
 * cookie with a 90-day expiry. This ensures CAPI match quality even
 * when the Meta Pixel is not installed alongside the SDK.
 */
export function getOrCreateFbp(): string | undefined {
  const existing = getCookie('_fbp');
  if (existing) return existing;

  if (generatedFbp) return generatedFbp;

  if (typeof document === 'undefined') return undefined;

  const timestamp = Date.now();
  const random = Math.floor(Math.random() * 10_000_000_000)
    .toString()
    .padStart(10, '0');
  const fbp = `fb.1.${timestamp}.${random}`;

  try {
    const expires = new Date(timestamp + 90 * 24 * 60 * 60 * 1000).toUTCString();
    document.cookie = `_fbp=${fbp}; path=/; expires=${expires}; SameSite=Lax`;
  } catch {
    // Cookie write failed — in-memory value still works for this page load
  }

  generatedFbp = fbp;
  return fbp;
}

/** @internal Reset the generated _fbp cache. For testing only. */
export function _resetFbpCache(): void {
  generatedFbp = undefined;
}

/**
 * Read TikTok's _ttp cookie.
 */
export function getTtpCookie(): string | undefined {
  return getCookie('_ttp');
}

/**
 * Capture the current page URL.
 * Required by Meta CAPI for every web event (event_source_url).
 */
export function getPageUrl(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    return window.location.href;
  } catch {
    return undefined;
  }
}

/**
 * Format an fbclid value into Meta's fbc cookie format.
 * Format: fb.1.{timestamp_ms}.{fbclid}
 *
 * @param fbclid - The raw fbclid parameter value from the URL
 * @param timestampMs - Optional timestamp in milliseconds (defaults to Date.now())
 */
export function formatFbc(fbclid: string, timestampMs?: number): string {
  const ts = timestampMs ?? Date.now();
  return `fb.1.${ts}.${fbclid}`;
}

/**
 * Get the fbc value. Checks in order:
 * 1. If an fbclid URL parameter is present, format it as fbc
 * 2. If an _fbc cookie exists, return it
 * 3. Return undefined
 */
export function getFbc(): string | undefined {
  // Check for fbclid in URL params
  if (typeof window !== 'undefined') {
    try {
      const params = new URLSearchParams(window.location.search);
      const fbclid = params.get('fbclid');
      if (fbclid) {
        return formatFbc(fbclid);
      }
    } catch {
      // URL parsing failed
    }
  }

  // Fall back to existing _fbc cookie
  return getCookie('_fbc');
}

/**
 * Build the full set of CAPI properties for an event.
 * All values are optional — only present keys are included.
 */
export function getCapiProperties(): Record<string, string> {
  const props: Record<string, string> = {};

  const fbp = getOrCreateFbp();
  if (fbp) props['$fbp'] = fbp;

  const ttp = getTtpCookie();
  if (ttp) props['$ttp'] = ttp;

  const pageUrl = getPageUrl();
  if (pageUrl) props['$page_url'] = pageUrl;

  const fbc = getFbc();
  if (fbc) props['$fbc'] = fbc;

  return props;
}
